// aiChatClientV2.ts - Version 2 using search engine

import { SpacesName } from "../stores/aiChatStore";
import { NewSearchResult } from "./aiChatClient";
import { SearchCriteria } from "./search_engine";

/**
 * Represents a single message in the chat history for V2.
 */
export interface ChatMessageV2 {
  uuid: string | undefined;
  role: "user" | "assistant" | "system";
  content: string;
  rawContent?: string;
  result?: NewSearchResult; // Use SearchCriteria instead of NewSearchResult
  requestId?: string;
  metadata?: {
    searchMode: "global" | "current";
    selectedSpaces?: string[];
    planInfo?: {
      propertyName: string;
      planName: string;
    };
    timestamp?: string;
  };
}

/**
 * Structure of the request body sent to the /ai/v2/chat endpoint.
 */
export interface ChatRequestBodyV2 {
  userPrompt: string;
  messageHistory?: ChatMessageV2[];
  spacesNames: SpacesName[];
  availableFields: string; // Fields available for search
}

/**
 * Structure of a successful response from the /api/ai/v2/chat endpoint.
 */
export interface ChatSuccessResponseV2 {
  aiResponse: SearchCriteria;
  rawResponse: string;
}

/**
 * Structure for error responses from the backend API.
 */
export interface BackendErrorResponseV2 {
  error: string;
  details?: string;
}

/**
 * Calls the backend AI chat V2 API endpoint.
 *
 * @param apiUrl - The full URL of the chat endpoint (e.g., 'http://localhost:3000/ai/v2/chat').
 * @param userPrompt - The user's current message.
 * @param messageHistory - An array of previous messages in the conversation.
 * @param spacesNames - Array of space names with their DBAs.
 * @param availableFields - String of available fields for search.
 * @returns A promise that resolves with the ChatSuccessResponseV2 object.
 * @throws An error if the API call fails or returns an error status.
 */
export async function callChatApiV2(
  apiUrl: string,
  userPrompt: string,
  messageHistory: ChatMessageV2[] = [],
  spacesNames: SpacesName[] = [],
  availableFields: string = "",
): Promise<ChatSuccessResponseV2> {
  const requestBody: ChatRequestBodyV2 = {
    userPrompt,
    messageHistory,
    spacesNames,
    availableFields,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorData: BackendErrorResponseV2 = {
        error: `HTTP error! Status: ${response.status}`,
      };
      try {
        // Try to parse backend error response
        errorData = await response.json();
      } catch (e) {
        // Ignore if parsing fails, use the basic HTTP error message
        console.warn("Could not parse error response body as JSON.");
      }
      // Combine error messages if details are available
      const errorMessage = errorData.details
        ? `${errorData.error} - Details: ${errorData.details}`
        : errorData.error;
      throw new Error(errorMessage);
    }

    const data: ChatSuccessResponseV2 = await response.json();

    // Basic validation of the successful response structure
    if (!data || typeof data !== "object") {
      throw new Error("Invalid response structure received from the API.");
    }

    return data;
  } catch (error) {
    console.error("Error calling chat API V2:", error);
    // Re-throw the error to be handled by the caller
    // Ensure it's an Error object
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("An unknown error occurred during the API call.");
    }
  }
}
